import { combineReducers } from 'redux';
import {
  dissoc,
  indexBy,
  lensPath,
  map,
  prop,
  set,
  symmetricDifference,
} from 'ramda';
import { reject, some, findIndex } from 'lodash/fp';
import moment from 'moment';

import { engageV2Reducer } from '../../modules/shared/engage/store';

import {
  CAMPAIGN_TYPE,
  AUDIENCE_TYPE,
  SET_CAMPAIGN,
  SET_CAMPAIGN_PROMO_ID,
  CLEAR_PROMO,
  SET_CAMPAIGN_TYPE,
  SET_CAMPAIGN_CUSTOMER_GROUP,
  SET_CAMPAIGN_SUBJECT,
  SET_CAMPAIGN_BODY,
  SET_CAMPAIGN_COLOR,
  TOGGLE_CAMPAIGN_SEND_EMAIL,
  TOGGLE_CAMPAIGN_SEND_SMS,
  TOGGLE_CAMPAIGN_SEND_PUSH,
  SET_CAMPAIGN_LOGO,
  SET_CAMPAIGN_BACKGROUND,
  SET_FETCHING,
  SET_CUSTOMER_GROUPS,
  SET_NEW_CUSTOMER_GROUP_WEEKS,
  SET_SERVICE_CUSTOMER_GROUP_SERVICE_ID,
  TOGGLE_CAMPAIGN_CUSTOMER_GROUP,
  SET_CAMPAIGN_LOGO_IMAGE,
  SET_CAMPAIGN_BACKGROUND_IMAGE,
  SET_SCHEDULED_AT,
  FETCH_CAMPAIGN_LIST,
  CLEAR_CAMPAIGN_LIST,
  SET_CAMPAIGN_OWNER_ID,
  SET_CAMPAIGN_OWNER_TYPE,
  FETCH_CAMPAIGN,
  RESET_CAMPAIGN,
  DELETE_CAMPAIGN,
  CAMPAIGN_FILTER_ALL,
  SET_FILTER,
  SET_RECURRING_START_DATE,
  SET_RECURRING_END_DATE,
  SET_RECURRING_FREQUENCY,
  SET_RECURRING_SEND_TO_ALL_CUSTOMERS,
  SET_RECURRING_TIME,
  SET_SEARCH_STRING,
} from './constants';
import { addItem, dropItem } from '../common/helpers/functional';

const initialCampaignState = {
  ownerId: null,
  ownerType: null,
  type: CAMPAIGN_TYPE.update,
  promoId: null,
  customerGroups: [{ name: AUDIENCE_TYPE.all }],
  barberId: null,
  subject: null,
  body: null,
  color: '#42bbb6',
  sendEmail: true,
  sendSms: true,
  sendPush: true,
  logo: null,
  logoImage: null,
  background: null,
  backgroundImage: null,
  customersNotifiedViaEmail: 0,
  customersNotifiedViaSms: 0,
  customersNotifiedViaPush: 0,
  totalCustomersNotified: 0,
  recurringNextOccurrenceDateTime: null,
};

const campaign = (state = initialCampaignState, { type, payload }) => {
  switch (type) {
    case RESET_CAMPAIGN:
      return initialCampaignState;
    case SET_CAMPAIGN:
      return payload;
    case SET_CAMPAIGN_TYPE:
      return { ...state, type: payload };
    case SET_CAMPAIGN_PROMO_ID:
      return { ...state, promoId: payload };
    case CLEAR_PROMO:
      return { ...state, promo: null };
    case SET_CAMPAIGN_CUSTOMER_GROUP:
      return { ...state, customerGroups: payload };
    case SET_NEW_CUSTOMER_GROUP_WEEKS: {
      const newGroupIndex = findIndex({ name: AUDIENCE_TYPE.new }, state.customerGroups);
      const lens = lensPath(['customerGroups', newGroupIndex, 'weeks']);
      return set(lens, payload, state);
    }
    case SET_SERVICE_CUSTOMER_GROUP_SERVICE_ID: {
      const newGroupIndex = findIndex({ name: AUDIENCE_TYPE.service }, state.customerGroups);
      const lens = lensPath(['customerGroups', newGroupIndex, 'serviceId']);
      return set(lens, payload, state);
    }
    case TOGGLE_CAMPAIGN_CUSTOMER_GROUP: {
      if (payload === AUDIENCE_TYPE.new && findIndex({ name: AUDIENCE_TYPE.new }, state.customerGroups)) {
        return { ...state, customerGroups: reject({ name: AUDIENCE_TYPE.new }) };
      }
      if (payload.name === AUDIENCE_TYPE.service && some({ name: AUDIENCE_TYPE.service }, state.customerGroups)) {
        return { ...state, customerGroups: reject({ name: AUDIENCE_TYPE.service }, state.customerGroups) };
      }
      return { ...state, customerGroups: symmetricDifference(state.customerGroups, [payload]) };
    }
    case SET_CAMPAIGN_SUBJECT:
      return { ...state, subject: payload };
    case SET_CAMPAIGN_BODY:
      return { ...state, body: payload };
    case SET_CAMPAIGN_COLOR:
      return { ...state, color: payload };
    case TOGGLE_CAMPAIGN_SEND_EMAIL:
      return { ...state, sendEmail: !state.sendEmail };
    case TOGGLE_CAMPAIGN_SEND_SMS:
      return { ...state, sendSms: !state.sendSms };
    case TOGGLE_CAMPAIGN_SEND_PUSH:
      return { ...state, sendPush: !state.sendPush };
    case SET_CAMPAIGN_LOGO:
      return { ...state, logo: payload };
    case SET_CAMPAIGN_LOGO_IMAGE:
      return { ...state, logoImage: payload };
    case SET_CAMPAIGN_BACKGROUND:
      return { ...state, background: payload };
    case SET_CAMPAIGN_BACKGROUND_IMAGE:
      return { ...state, backgroundImage: payload };
    case SET_CAMPAIGN_OWNER_ID:
      return { ...state, ownerId: payload };
    case SET_CAMPAIGN_OWNER_TYPE:
      return { ...state, ownerType: payload };
    default:
      return state;
  }
};

const isFetching = (state = false, { type, payload }) => {
  switch (type) {
    case SET_FETCHING:
      return !!payload;
    default:
      return state;
  }
};

const initialCustomerGroupsState = {
  new: {
    weeks: 6,
  },
  service: {
    serviceId: '',
  },
};
const customerGroups = (state = initialCustomerGroupsState, { type, payload }) => {
  switch (type) {
    case RESET_CAMPAIGN:
      return initialCustomerGroupsState;
    case SET_CUSTOMER_GROUPS: {
      const newState = payload || {};
      return {
        ...newState,
        new: {
          ...newState.new,
          weeks: state.new.weeks,
        },
        service: {
          ...newState.service,
          serviceId: state.service.serviceId,
        },
      };
    }
    case SET_NEW_CUSTOMER_GROUP_WEEKS:
      return {
        ...state,
        new: {
          ...state.new,
          weeks: payload,
        },
      };
    case SET_SERVICE_CUSTOMER_GROUP_SERVICE_ID:
      return {
        ...state,
        service: {
          ...state.service,
          serviceId: payload,
        },
      };
    default:
      return state;
  }
};

const initialCampaignChargeState = {
  scheduledAt: null,
  recurringStartDate: moment(Date.now()).format('YYYY-MM-DD'),
  recurringEndDate: null,
  recurringFrequency: 1,
  recurringSendToAllCustomers: false,
  recurringTime: '00:00',
};

const campaignCharge = (state = initialCampaignChargeState, { type, payload }) => {
  switch (type) {
    case RESET_CAMPAIGN:
      return initialCampaignChargeState;
    case SET_SCHEDULED_AT:
      return { ...state, scheduledAt: payload };
    case SET_RECURRING_START_DATE:
      return { ...state, recurringStartDate: payload };
    case SET_RECURRING_END_DATE:
      return { ...state, recurringEndDate: payload };
    case SET_RECURRING_FREQUENCY:
      return { ...state, recurringFrequency: parseInt(payload, 10) };
    case SET_RECURRING_SEND_TO_ALL_CUSTOMERS:
      return { ...state, recurringSendToAllCustomers: payload };
    case SET_RECURRING_TIME:
      return { ...state, recurringTime: payload };
    default:
      return state;
  }
};

const campaignListById = (state = {}, { type, payload }) => {
  switch (type) {
    case CLEAR_CAMPAIGN_LIST:
      return {};
    case FETCH_CAMPAIGN_LIST:
      return indexBy(prop('id'), payload);
    case FETCH_CAMPAIGN:
      if (!payload || !payload.id) return state;
      return {
        ...state,
        [payload.id]: payload,
      };
    case DELETE_CAMPAIGN:
      if (!payload || !payload.id) return state;
      return dissoc(payload.id, state);
    default:
      return state;
  }
};

const campaignListIds = (state = [], { type, payload }) => {
  switch (type) {
    case CLEAR_CAMPAIGN_LIST:
      return [];
    case FETCH_CAMPAIGN_LIST:
      return map(prop('id'), payload);
    case FETCH_CAMPAIGN:
      if (!payload || !payload.id) return state;
      return addItem(payload.id, state);
    case DELETE_CAMPAIGN:
      if (!payload || !payload.id) return state;
      return dropItem(payload.id, state);
    default:
      return state;
  }
};

const campaignList = combineReducers({
  byId: campaignListById,
  list: campaignListIds,
});

const filter = (state = CAMPAIGN_FILTER_ALL, { type, payload }) => {
  if (type === SET_FILTER) return payload;
  return state;
};

const searchString = (state = '', { type, payload }) => {
  if (type === SET_SEARCH_STRING) return payload;
  return state;
};

const reducer = combineReducers({
  campaign,
  customerGroups,
  isFetching,
  campaignCharge,
  campaignList,
  filter,
  searchString,
  engageV2: engageV2Reducer,
});

export default reducer;
